import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { Button, Form, Alert, Spinner } from 'react-bootstrap'
import { Link, useLocation, useParams } from 'react-router-dom'

import AuthSplitLayout from 'components/layout/auth/authSplitLayout'
import PasswordInput from 'components/forms/passwordInput'
import authLinksCss from 'components/layout/auth/authLinks.module.scss'
import clientApi from 'api/clientApi'
import { isValidEmail } from 'utilities/email.util'
import { AppMessage } from '../../../utilities/messages'
import { completeLogin } from '../completeLogin'

const apiEndpoint = process.env.REACT_APP_REPASSE_API_ENDPOINT

export const SignIn: FunctionComponent<{
  handleSuccessfulLogin: () => void
}> = ({ handleSuccessfulLogin }) => {
  const location = useLocation()
  const { sessionExpired } = useParams<{ sessionExpired?: string }>()
  const [email, setEmail] = useState<string>()
  const [password, setPassword] = useState<string>()
  const [emailSet, setEmailSet] = useState(false)
  const [validEmail, setValidEmail] = useState(false)
  const [passwordSet, setPasswordSet] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loginError, setLoginError] = useState(false)
  const [tokenExpired, setTokenExpired] = useState(false)
  const [entering, setEntering] = useState(false)
  const [appMessage, setAppMessage] = useState<AppMessage>({ show: false })

  const showStateMessage = useCallback(() => {
    if (location.state && location.state.message) {
      setAppMessage({
        show: true,
        success: true,
        message: location.state.message,
      })
    }

    if (location.state && location.state.email) {
      setEmail(location.state.email)
      setValidEmail(isValidEmail(location.state.email))
    }
  }, [location.state])

  useEffect(() => {
    setTokenExpired(!!sessionExpired)
    showStateMessage()

    if (new URLSearchParams(location.search).get('accountRemoved')) {
      setAppMessage({ show: true, success: true, message: 'Sua conta foi excluída.' })
    }
  }, [sessionExpired, showStateMessage, location.search])

  const login = async () => {
    if (!preValidateCredentials()) {
      return
    }

    try {
      setEntering(true)

      const { headers } = await clientApi.post(`/${apiEndpoint}/login`, {
        username: email,
        password: password,
      })

      successfulLogin(headers.authorization)
    } catch (error) {
      console.error(error)
      setLoginError(true)
      setEntering(false)
    }
  }


  const successfulLogin = async (token: string) => {
    setLoginError(false)
    setEntering(false)

    try {
      await completeLogin(token, handleSuccessfulLogin)
    } catch (error) {
      console.error(error)
    }
  }

  const preValidateCredentials = (): boolean => {
    const emailSet = !!email && email.trim().length > 0
    const passwordSet = !!password && password.trim().length > 0

    setSubmitted(true)
    setEmailSet(!!email)
    setPasswordSet(!!password)

    return emailSet && validEmail && passwordSet
  }

  const validateEmail = () => {
    if (email) {
      setEmailSet(true)
      setValidEmail(isValidEmail(email))
    }
  }

  return (
    <AuthSplitLayout>
      <h1 className="page-title">Entrar na sua conta</h1>
      <p className="page-subtitle">Bem-vindo de volta. Informe seus dados para continuar.</p>

      <div className="mt-4">
        {tokenExpired && (
          <Alert variant="warning">Sessão expirada, faça o login novamente.</Alert>
        )}

        {loginError && <Alert variant="danger">Email ou senha incorreto.</Alert>}

        {appMessage.show && (
          <Alert variant={appMessage.success ? 'success' : 'danger'}>
            {appMessage.message}
          </Alert>
        )}
      </div>

      <Form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          login()
        }}
      >
        <Form.Group className="mb-3" controlId="login-email">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            name="email"
            size="lg"
            placeholder="seu@email.com"
            autoComplete="email"
            value={email}
            isInvalid={(submitted && !emailSet) || (!validEmail && emailSet)}
            onChange={(event) => setEmail(event.target.value)}
            onBlur={validateEmail}
          />
          {submitted && !emailSet && (
            <span className="invalid-feedback d-block">Informe seu email</span>
          )}
          {!validEmail && emailSet && (
            <span className="invalid-feedback d-block">
              Informe um email válido
            </span>
          )}
        </Form.Group>

        <Form.Group controlId="login-password">
          <div className="d-flex align-items-center justify-content-between">
            <Form.Label>Senha</Form.Label>
            <Link
              to="/password-recovery"
              className="small text-decoration-none text-muted mb-2"
              // Tab goes straight from email to password.
              tabIndex={-1}
            >
              Esqueceu a senha?
            </Link>
          </div>
          <PasswordInput
            name="password"
            size="lg"
            placeholder="Digite sua senha"
            autoComplete="current-password"
            isInvalid={submitted && !passwordSet}
            onChange={(event) => setPassword(event.target.value)}
          />
          {submitted && !passwordSet && (
            <span className="invalid-feedback d-block">Informe sua senha</span>
          )}
        </Form.Group>

        <Button type="submit" size="lg" className="w-100 mt-4" disabled={entering}>
          {entering && <Spinner size="sm" animation="border" />}
          {entering ? 'Entrando…' : 'Entrar'}
        </Button>
      </Form>

      <p className={`${authLinksCss.links} ${authLinksCss.center}`}>
        <span>
          Não tem conta?{' '}
          <Link to="/register" className={authLinksCss.strong}>
            Cadastre-se
          </Link>
        </span>
      </p>
    </AuthSplitLayout>
  )
}

export default SignIn
